"use client";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useId, useState } from "react";

import { getUrls } from "@portal/app/_common/lib/urls";

import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import { styled } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";
import Avatar from "@mui/material/Avatar";
import { useRouter } from "next/navigation";
import { LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT } from "@portal/app/_components/ui-elements/Layout";
import { logout } from "@portal/app/_components/apolloUtils";
import { isProduction } from "@portal/app/_common/lib/edgeUtils";
import Skeleton from "@mui/material/Skeleton";

type ProfileButtonProps = {
  isMobileMenuOpen: boolean;
  setMobileMenuOpen: (value: boolean) => void;
  onOpenExperimentsLab: () => void;
};

export const ProfileButton = ({
  isMobileMenuOpen,
  setMobileMenuOpen,
  onOpenExperimentsLab,
}: ProfileButtonProps) => {
  const router = useRouter();
  const {
    loading: authLoading,
    loggedIn,
    user,
    role: { isCustomer, hasAdminRole },
    openSignInModal,
  } = useAuthenticatedContext();
  const accountMenuId = useId();
  const [accountMenuEl, setAccountMenuEl] = useState<HTMLElement>();
  const initials = [user?.nameFirst?.charAt(0), user?.nameLast?.charAt(0)].filter(Boolean).join("");
  const urls = getUrls("CUSTOMER");

  return (
    <>
      {authLoading ? (
        <Skeleton variant="circular" sx={{ width: "2.25rem", height: "2.25rem" }} />
      ) : loggedIn && user ? (
        <StyledProfileButton
          onClick={(e) => setAccountMenuEl(e.currentTarget)}
          data-testid="nav-bar-logged-in"
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={!!accountMenuEl}
          aria-controls={accountMenuEl ? accountMenuId : undefined}
          $isMobileMenuOpen={isMobileMenuOpen}
        >
          <UserAvatar>{initials}</UserAvatar>
        </StyledProfileButton>
      ) : (
        <StyledProfileButton
          onClick={() => {
            setMobileMenuOpen(false);
            openSignInModal("CREATE_ACCOUNT");
          }}
          data-testid="nav-bar-sign-in"
          aria-label="Sign in"
          $isMobileMenuOpen={isMobileMenuOpen}
        >
          <UserAvatar>
            <FontAwesomeIcon icon={faUser} />
          </UserAvatar>
        </StyledProfileButton>
      )}
      <Menu
        id={accountMenuId}
        slotProps={{ list: { "aria-label": "Account" } }}
        anchorEl={accountMenuEl}
        open={!!accountMenuEl}
        onClose={() => setAccountMenuEl(undefined)}
        sx={{ marginTop: "0.5rem" }}
      >
        {[
          ...(isCustomer
            ? [
                <MenuItem
                  dense
                  onClick={() => {
                    router.push(urls.ManageAccount());
                  }}
                  key="manage-account"
                >
                  Manage account
                </MenuItem>,
              ]
            : []),
          ...(!isProduction() || hasAdminRole
            ? [
                <MenuItem
                  dense
                  onClick={() => {
                    onOpenExperimentsLab();
                    setAccountMenuEl(undefined);
                  }}
                  key="experiments-lab-menu-item"
                >
                  Experiments lab
                </MenuItem>,
              ]
            : []),
          <MenuItem dense onClick={() => logout()} key="sign-out">
            Sign out
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

const StyledProfileButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "$isMobileMenuOpen",
})<{ $isMobileMenuOpen: boolean }>`
  font-size: 1rem;
  border-radius: 0.5rem;
  transition: 100ms linear all;
  display: flex;
  margin: 0;
  justify-content: start;
`;

const UserAvatar = styled(Avatar)`
  width: 2.25rem;
  height: 2.25rem;
  font-size: 0.9em;
  font-weight: bold;
  background: ${({ theme }) => theme.palette.primary.main};

  ${({ theme }) => theme.breakpoints.up(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT)} {
    width: 2.5rem;
    height: 2.5rem;
  }

  .withTransparentBackground:not(.mobileOpen) & {
    border: ${({ theme }) => `1px solid ${theme.palette.primary.light}`};
  }
`;
